import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme.ts';
import { DATE_FORMATS, formatStringDateToUI } from '@dealstack/shared/lib/format/dates/date.ts';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds.ts';
import {
	BankAccountStatus,
	MipInvestorBankAccountReviewRes,
} from '@dealstack/shared/lib/generated';
import { Button, Flex, Text } from '@mantine/core';
import { LONG_DASH } from '@dealstack/frontend/shared/other/react/specialSigns.tsx';

interface Props {
	review: MipInvestorBankAccountReviewRes;
	disabled: boolean;
	onRevoke: () => void;
}

export const ReviewDecisionCard = ({ review, disabled, onRevoke }: Props) => {
	const tMipManage = useTranslationMipManage();

	const isApproved = review.decision === BankAccountStatus.APPROVED;
	const accentColor = isApproved ? defaultTheme.v3.colors.teal[8] : defaultTheme.v3.colors.red[6];

	return (
		<Flex direction="column" gap="1rem" px="1rem" pb="1rem">
			<Flex
				direction="column"
				gap="0.75rem"
				p="1rem"
				style={{ border: `1px solid ${accentColor}`, borderRadius: '0.25rem' }}
				data-testid={TEST_ELEMENT_IDS.MIP_MANAGE_BANK_ACCOUNT_DECISION_CARD}
			>
				<Flex align="center" gap="0.5rem">
					<Icon
						variant={isApproved ? 'holdings-checkmark' : 'close'}
						size="vlg"
						color={accentColor}
					/>
					<Text size="md" fw={600} c="dark.8">
						{tMipManage(
							isApproved
								? 'bank_accounts.review.approved'
								: 'bank_accounts.review.declined'
						)}
					</Text>
				</Flex>

				<DecisionRow
					label={tMipManage(
						isApproved
							? 'bank_accounts.review.approved_by'
							: 'bank_accounts.review.declined_by'
					)}
					value={review.decidedByName ?? LONG_DASH}
				/>
				<DecisionRow
					label={tMipManage('bank_accounts.review.timestamp')}
					value={
						formatStringDateToUI(review.decidedAt, DATE_FORMATS.DATE_TIME_ALT) ??
						LONG_DASH
					}
				/>
				{!!review.reason && (
					<Flex direction="column" gap="0.25rem">
						<Text size="sm" c="dark.0">
							{tMipManage('bank_accounts.review.reason')}
						</Text>
						<Text
							size="sm"
							fw={600}
							c="dark.8"
							style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
							data-testid={TEST_ELEMENT_IDS.MIP_MANAGE_BANK_ACCOUNT_DECISION_REASON}
						>
							{review.reason}
						</Text>
					</Flex>
				)}
			</Flex>

			<Flex justify="center">
				<Button
					variant="subtle"
					disabled={disabled}
					onClick={onRevoke}
					data-testid={TEST_ELEMENT_IDS.MIP_MANAGE_BANK_ACCOUNT_REVOKE_BUTTON}
					color="red"
					leftSection={<Icon variant="refresh-v3" size="lg" />}
				>
					{tMipManage(
						isApproved
							? 'bank_accounts.review.revoke_approval'
							: 'bank_accounts.review.revoke_decline'
					)}
				</Button>
			</Flex>
		</Flex>
	);
};

const DecisionRow = ({ label, value }: { label: string; value: string }) => (
	<Flex justify="space-between" gap="1rem" align="flex-start">
		<Text size="sm" c="dark.0">
			{label}
		</Text>
		<Text size="sm" fw={600} c="dark.8" ta="right">
			{value}
		</Text>
	</Flex>
);
